import type { ColDef, ICellRendererParams } from 'ag-grid-community'
import { PROCUREMENT_MATRICES } from '@crm/shared'
import type { RequestItemStatus, RequestItemPriority } from '../../../shared/api/types'
import { StatusCell } from './StatusCell'
import { PriorityCell } from './PriorityCell'
import { AssignSelect } from './AssignSelect'
import { ProcNoteCell } from './ProcNoteCell'
import { TextareaEditor } from '../../table/TextareaEditor'
import type { ItemColDefsParams, ItemRow } from './itemColDefs.types'

/**
 * Правая часть сетки позиций: приоритет, статус, назначение, сводка квот и
 * комментариев, примечание к КП и колонка заметки закупщика (только у него самого).
 * Вынесено из itemColDefs.tsx ради правила «файлы ≤500 строк», поведение прежнее.
 */
export function buildProcItemColDefs({
  isAdmin,
  userRole,
  userId,
  isProcurement,
  requestId,
  procUsers,
  handleProcNoteHeight,
  requestManager,
  clientCategory,
}: ItemColDefsParams): ColDef<ItemRow>[] {
  return [
    {
      colId: 'priority',
      // field=priorityLabel — фильтр/сортировка по русскому названию (Высокий/
      // Средний/Базовый). Сам priority остаётся в data, читается cellRenderer.
      field: 'priorityLabel',
      headerName: 'Приор.',
      cellRenderer: (p: ICellRendererParams<ItemRow>) => (
        <PriorityCell
          itemId={p.data!.id as string}
          currentPriority={p.data!.priority as RequestItemPriority}
          requestId={requestId}
          clientCategory={clientCategory}
        />
      ),
    },

    {
      colId: 'status',
      // field=statusLabel — фильтр/сортировка по русскому статусу (Готова к КП,
      // КП выставлено и т.п.). Сам status остаётся в data, читается StatusCell.
      field: 'statusLabel',
      headerName: 'Статус',
      cellRenderer: (p: ICellRendererParams<ItemRow>) => {
        const results = (p.data!.results as { id: string }[] | undefined) ?? []
        const hasAnyQuote = results.length > 0 || ((p.data!.resultsCount as number) ?? 0) > 0
        return (
          <StatusCell
            itemId={p.data!.id as string}
            currentStatus={p.data!.status as RequestItemStatus}
            requestId={requestId}
            userRole={userRole}
            teamProcurementState={
              p.data!.teamProcurementState as
                | import('@crm/shared').RequestItemProcurementState
                | undefined
            }
            procurementState={
              p.data!.procurementState as
                | import('@crm/shared').RequestItemProcurementState
                | undefined
            }
            hasAnyQuote={hasAnyQuote}
          />
        )
      },
    },

    {
      colId: 'assigned',
      // field=assignedToEmail — фильтр по человекочитаемому email, не по UUID.
      field: 'assignedToEmail',
      headerName: 'Назначен',
      sortable: false,
      cellRenderer: (p: ICellRendererParams<ItemRow>) => {
        const row = p.data!
        const email = (row.assignedToEmail as string) || '—'
        // Продажи — выпадашка с пунктом «(менеджер)». РОЗ (№50, 29.09.2026) —
        // себя и свою команду, только позиции «Китай»/«Производители».
        const isSales =
          isAdmin || userRole === 'MANAGER' || userRole === 'ROP' || userRole === 'ASSISTANT'
        const rosAssigns = userRole === 'ROS' && PROCUREMENT_MATRICES.includes(row.matrix as never)
        if (!isSales && !rosAssigns) {
          return <span className="text-xs text-gray-600">{email}</span>
        }
        return (
          <AssignSelect
            itemId={row.id as string}
            currentAssignedId={row.assignedToId as string | null}
            currentAssignedLabel={email === '—' ? null : email}
            requestId={requestId}
            requestManager={isSales ? requestManager : null}
            bulk={!isSales}
          />
        )
      },
    },

    {
      colId: 'kpcomm',
      headerName: isProcurement ? 'Квота' : 'Квота/💬',
      sortable: false,
      wrapText: !isProcurement,
      autoHeight: !isProcurement,
      cellRenderer: (p: ICellRendererParams<ItemRow>) => {
        const results =
          (p.data!.results as
            | {
                id: string
                sourceType: string
                createdBy?: string
                creator?: { id: string; name?: string | null; email: string } | null
              }[]
            | undefined) ?? []

        if (isProcurement) {
          const mine = results.filter((r) => (r.creator?.id ?? r.createdBy) === userId)
          const count = mine.length
          return (
            <span
              className={`text-xs font-medium ${count > 0 ? 'text-green-600' : 'text-gray-400'}`}
              title={count > 0 ? 'Мои квоты' : 'Нет моих квот'}
            >
              {count > 0 ? `✓ ${count}` : '—'}
            </span>
          )
        }

        const byUser = new Map<string, { name: string; count: number; sources: Set<string> }>()
        for (const r of results) {
          if (r.sourceType !== 'CHINA' && r.sourceType !== 'MANUFACTURER') continue
          const uid = r.creator?.id ?? r.createdBy ?? 'unknown'
          const label = r.creator?.name || r.creator?.email?.split('@')[0] || 'закуп.'
          const entry = byUser.get(uid) ?? { name: label, count: 0, sources: new Set<string>() }
          entry.count += 1
          entry.sources.add(r.sourceType)
          byUser.set(uid, entry)
        }
        const quoters = Array.from(byUser.values())

        return (
          <div className="text-xs whitespace-normal leading-tight py-0.5">
            {quoters.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-0.5">
                {quoters.map((q, i) => (
                  <span
                    key={i}
                    className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-green-50 text-green-700 border border-green-200 text-[10px]"
                    title={`${q.count} квот (${Array.from(q.sources).join(', ')})`}
                  >
                    ✓ {q.name}
                    {q.count > 1 && <span className="text-green-500">×{q.count}</span>}
                  </span>
                ))}
              </div>
            )}
            <span className="text-gray-400 whitespace-nowrap">
              {(p.data!.resultsCount as number) > 0 && quoters.length === 0 && (
                <span className="text-green-600 font-medium mr-1">
                  ✓{p.data!.resultsCount as number}
                </span>
              )}
              {(p.data!.commentsCount as number) > 0 && (
                <span title={`${p.data!.commentsCount} комм.`}>
                  💬{p.data!.commentsCount as number}
                </span>
              )}
            </span>
          </div>
        )
      },
    },

    {
      colId: 'comment',
      field: 'comment',
      headerName: 'Комментарий',
      editable: isAdmin || userRole === 'MANAGER' || userRole === 'ROP' || userRole === 'ASSISTANT',
      wrapText: true,
      autoHeight: true,
      cellClass: 'text-xs text-gray-600 whitespace-pre-wrap leading-snug',
      cellEditor: TextareaEditor,
      cellEditorPopup: true,
      cellEditorPopupPosition: 'over',
      valueFormatter: (p) => (p.value as string) || '',
      valueSetter: (p) => {
        p.data.comment = p.newValue as string
        return true
      },
    },

    // «Примечание к КП» — текст попадает в столбец «Примечание» предпросмотра КП
    // и в сам файл КП (Excel/PDF). Видят и правят ТОЛЬКО продажные роли
    // (ADMIN/MANAGER/ROP/ASSISTANT) — для закупки/ГД колонка не добавляется.
    ...(isAdmin || userRole === 'MANAGER' || userRole === 'ROP' || userRole === 'ASSISTANT'
      ? ([
          {
            colId: 'kpNote',
            field: 'kpNote',
            headerName: 'Примечание к КП',
            editable: true,
            wrapText: true,
            autoHeight: true,
            cellClass: 'text-xs text-gray-600 whitespace-pre-wrap leading-snug',
            cellEditor: TextareaEditor,
            cellEditorPopup: true,
            cellEditorPopupPosition: 'over',
            valueFormatter: (p) => (p.value as string) || '',
            valueSetter: (p) => {
              p.data.kpNote = p.newValue as string
              return true
            },
          },
        ] as ColDef<ItemRow>[])
      : []),

    // Колонка заметки закупщика — только у самого закупщика и только своя.
    // Остальным ролям скрыта с 26.09.2026: на проде ни одной заметки не было
    // (таблица procurement_table_notes пуста), а 4 пустые колонки раздували сетку.
    ...procUsers
      .filter((pu) => userRole === 'PROCUREMENT' && pu.id === userId)
      .map(
        (pu): ColDef<ItemRow> => ({
          colId: `proc_${pu.id}`,
          field: `proc_${pu.id}`,
          headerName: pu.name || pu.email,
          wrapText: true,
          autoHeight: true,
          cellClass: 'text-xs text-gray-600',
          cellRenderer: (p: ICellRendererParams<ItemRow>) => (
            <ProcNoteCell
              itemId={p.data!.id as string}
              initialNote={(p.data![`proc_${pu.id}`] as string) ?? ''}
              onHeightChange={handleProcNoteHeight}
            />
          ),
        }),
      ),
  ]
}
